import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk'
import { useFeatureFlag } from '@/composables/featureFlags.js'
import { useAuth } from '@/composables/useAuth.js'
import { useIsVetera } from '@/composables/useBrand.js'
import { getUserFullName } from '@/utils/user.js'
import { getCurrentTimestamp } from '@/utils/date/getter.js'
import { fetchApiRequest } from '@/utils/api-request.js'

// Support communications setting value for intercom
const SUPPORT_COMMUNICATIONS_INTERCOM = 4
const VETERA_MESSENGER_STYLE_ID = '5647'

export function useIntercom() {
  if (!import.meta.client) {
    return
  }

  const initializedProvetId = ref<string | null>(null)

  const config = useRuntimeConfig()
  const route = useRoute()
  const provetId = useRouteParams<string>('provetId')
  const { user, getSetting } = useAuth()
  const featureFlag = useFeatureFlag('temporary.intercom.support_chat')
  const isVetera = useIsVetera()

  const userFullName = computed(() => getUserFullName(user.value?.attributes))

  async function initializeIntercom(provetId: string) {
    const appId = config.public.intercom?.appId
    const supportCommunications = getSetting('support_communications')

    if (
      !featureFlag.value ||
      !appId ||
      supportCommunications !== SUPPORT_COMMUNICATIONS_INTERCOM
    ) {
      return
    }

    try {
      // API path for intercom token (not in OpenAPI schema; use fetchApiRequest)
      const response = await fetchApiRequest(
        'GET',
        `/${provetId}/organization/administrative/intercom_token/`,
      )
      if (!response.ok) {
        return
      }
      const { token } = (await response.json()) as { token: string }
      if (!token) {
        return
      }

      if (initializedProvetId.value && initializedProvetId.value !== provetId) {
        update({ user_jwt: token })
      } else {
        Intercom({
          app_id: appId,
          name: userFullName.value || undefined,
          region: 'eu',
          session_duration: 86400000,
          user_jwt: token,
          ...(isVetera.value && {
            messenger_style_id: VETERA_MESSENGER_STYLE_ID,
          }),
        })
      }

      initializedProvetId.value = provetId
    } catch {
      // Silently fail - Intercom is non-critical
    }
  }

  const initializeIntercomDebounced = useDebounceFn(initializeIntercom, 3000)

  function scheduleInit(provetId: string) {
    // Do if browser supports, debounce if not
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        void initializeIntercom(provetId)
      })
    } else {
      void initializeIntercomDebounced(provetId)
    }
  }

  watchDebounced(
    provetId,
    (id) => {
      if (!id || initializedProvetId.value === id) {
        return
      }

      scheduleInit(id)
    },
    { debounce: 300, immediate: true },
  )

  watch(
    () => route.fullPath,
    () => {
      if (initializedProvetId.value) {
        update({ last_request_at: Math.floor(getCurrentTimestamp() / 1000) })
      }
    },
  )

  // Cleanup on unmount
  onUnmounted(() => {
    if (initializedProvetId.value) {
      shutdown()
    }
  })
}
